import { Sprite, Text, Point, Container, Graphics } from 'pixi.js';
import { getLocalizedText, getLocalizedValue } from '@softgames/localization';
import { LayoutText } from '../../../libs/layouts/populateLayouts/LayoutText';
import { waitForNextFrame } from '../../../utils/async-utils';
import type { LevelTaskProgress } from '../../tasks/types';
import { createTasksViewGroup } from '../../tasks/views/createTasksViewGroup';
import { GoalPreviewTaskLayout } from '../../../../layouts/taskViews/GoalPreviewTaskLayout';
import { registerResizableObject } from '../../../utils/pixi/registerResizableObject';
import { soundManager } from '../../../app/SoundManager';

interface GoalPreviewOverlayOptions {
  readonly tasks: LevelTaskProgress[];
  readonly levelNumber: number;
  readonly fadeOutBackground?: boolean;
}

export class GoalPreviewOverlayView extends Container {
  private readonly blackOverlay: Graphics = new Graphics().rect(0, 0, 500, 500).fill({ color: 'black' }).props({
    parent: this,
    alpha: 0.6,
  });

  private readonly content = new Container().props({
    parent: this,
  });

  private readonly bannerBg: Sprite = Sprite.from('Blue_Header').props({
    parent: this.content,
    anchor: { x: 0.5, y: 0.5 },
    scaleY: 1.2,
  });

  private readonly foreground = new Container().props({
    parent: this.content,
  });

  private readonly levelText = new Text({
    text: 'Level -',
    style: {
      fill: 0xffffff,
      fontSize: 70,
      stroke: {
        width: 7,
        alignment: 0,
        cap: 'round',
        join: 'round',
        miterLimit: 10,
        color: 0x3473d1,
      },
      align: 'left',
      fontFamily: 'Baloo',
    },
  }).props({
    parent: this.content,
    anchor: { x: 0.5, y: 0.5 },
    y: -170,
  });

  constructor(private readonly options: GoalPreviewOverlayOptions) {
    super();

    this.createForegroundElements(options.tasks);

    this.levelText.props({
      visible: options.levelNumber > 0,
    });
    this.levelText.text = `${getLocalizedText('Level')} ${getLocalizedValue(options.levelNumber)}`;

    registerResizableObject(this);
  }

  public animateAndDestroy(): Promise<void> {
    const { fadeOutBackground = true } = this.options;
    const globalPos = new Point(0, 0);
    const localPos = this.content.toLocal(globalPos);

    return new Promise((resolve) => {
      this.animatePopIn(() => {
        this.animateToTopBar(localPos, fadeOutBackground, 1200, () => {
          resolve();

          void waitForNextFrame().then(() => {
            this.parent?.removeChild(this);
            this.destroy();
          });
        });
      });
    });
  }

  public destroy(): void {
    if (this.destroyed) return;
    this.removeTweens(true);
    super.destroy({ children: true });
  }

  private createForegroundElements(tasks: LevelTaskProgress[]): void {
    // A new Container is created for center aligning / scaling elements.
    const parent = new Container().props({
      parent: this.foreground,
    });

    new LayoutText({
      text: getLocalizedText('Goal'),
      style: {
        fill: 0xffffff,
        fontSize: 60,
        stroke: {
          width: 7,
          alignment: 0,
          cap: 'round',
          join: 'round',
          miterLimit: 10,
          color: 0x3473d1,
        },
        align: 'right',
        fontFamily: 'Baloo',
      },
    })
      .props({
        parent,
        x: -20,
        anchor: { x: 1, y: 0.5 },
      })
      .setTextSizeRestrictions({
        maxWidth: 170,
        maxFontSize: 60,
      });

    const tasksViewGroup = createTasksViewGroup(tasks, {
      forceBasic: true,
      constructLayout: () => new GoalPreviewTaskLayout(),
    });

    tasksViewGroup.container.props({
      parent,
      scale: 1.35,
      x: -tasksViewGroup.container.getLocalBounds().minX * 1.35,
      y: 4,
    });

    // Scale down taskViews if too wide.
    if (tasksViewGroup.container.width > 500) {
      const scale = 500 / tasksViewGroup.container.width;
      tasksViewGroup.container.props({
        scale: { x: scale, y: scale },
        x: -tasksViewGroup.container.getLocalBounds().minX * scale,
      });
    }

    // adjust x if foreground content to wide.
    if (parent.width > 300) parent.x -= (parent.width - 300) / 2;
  }

  private animatePopIn(callback: () => void, delay = 0): void {
    const { foreground, levelText } = this;
    const fadeDuration = 400;

    this.alpha = 0;

    foreground.props({
      scale: 1.3,
    });

    levelText.props({
      scale: 1.3,
    });

    this.tweenTo({ alpha: 1 }, fadeDuration, 'quadOut', true, delay);

    this.tweens.create(levelText).to({ scaleX: 1, scaleY: 1 }, 1200, 'bounceOut', true, delay);

    const foregroundsScaleTween = this.tweens
      .create(foreground)
      .to({ scaleX: 1, scaleY: 1 }, 1200, 'bounceOut', true, delay);

    foregroundsScaleTween.onComplete.addOnce(callback);

    soundManager.playDelayed('UI_Goal_Banner_Appear', 300);
  }

  private animateToTopBar(topBarPos: Point, fadeOutBackground: boolean, delay: number, callback: () => void): void {
    const { blackOverlay, content } = this;
    const layoutFadeDuration = 300;
    const levelTextFadeDuration = 200;

    if (fadeOutBackground) {
      blackOverlay.tweenTo(
        { alpha: 0 },
        layoutFadeDuration,
        'quadOut',
        true,
        delay + Math.max(400 - layoutFadeDuration, 0),
      );
    }

    soundManager.playDelayed('UI_Goal_banner_FlyAway', delay + 250);

    const moveTween = content.tweenTo({ y: topBarPos.y }, 400, 'backIn', true, delay);
    moveTween.onComplete.addOnce(callback);

    this.levelText.tweenTo(
      { alpha: 0 },
      levelTextFadeDuration,
      'quadOut',
      true,
      Math.max(delay - levelTextFadeDuration, 0),
    );
  }

  public resize(width: number, height: number): void {
    const { blackOverlay, content, bannerBg } = this;
    blackOverlay.width = width;
    blackOverlay.height = height;
    content.x = width / 2;
    content.y = height / 2;
    bannerBg.width = width * 1.4;
  }
}
